/* Vovlift Lift Finder — rules engine (prototype). Pure functions, no DOM. Works in browser (window.VLLift) and node (module.exports).
   Principle: never a false "won't fit". A check returns 'no' only when (1) the lift value comes from the manufacturer
   (page / spec sheet / manual) AND (2) the vehicle/garage value was typed by the customer or comes from an exact
   manufacturer spec for that version AND (3) the difference exceeds the tolerance. Everything else is 'cond'. */
(function (root) {
  'use strict';

  var TOL = { weightPct: 0, ceiling: 0.5, width: 1, length: 1, driveThru: 0.5, wheelbase: 1, runway: 2, overhead: 0.5, underCar: 0.5 };
  var MFR = { mfr_page: 1, mfr_pdf: 1, vl_pdf: 1 }; // vl_pdf = manufacturer manual hosted on our CDN
  var CAT_LABEL = {
    two_post: '2-post lift', four_post: '4-post lift', scissor_full_rise: 'Full-rise scissor lift', scissor_mid_rise: 'Mid-rise scissor lift',
    scissor_low_rise: 'Low-rise lift', single_post: 'Single-post lift', motorcycle: 'Motorcycle / ATV lift'
  };

  // ---- helpers -------------------------------------------------------------------------------------------
  function num(x) { var n = typeof x === 'number' ? x : parseFloat(x); return isFinite(n) ? n : null; }
  function fv(lift, k) { var f = lift.f && lift.f[k]; if (f == null) return null; return Array.isArray(f) ? num(f[0]) : num(f.v); }
  function fs(lift, k) { var f = lift.f && lift.f[k]; if (f == null) return null; return Array.isArray(f) ? f[0] : f.v; }
  function fromMfr(lift, k) { var f = lift.f && lift.f[k]; if (f == null) return false; var s = Array.isArray(f) ? f[1] : f.src; return s === 'm' || !!MFR[s]; }
  function r1(x) { return Math.round(x * 10) / 10; }
  function inch(x) { return (Math.round(x * 10) / 10).toString().replace(/\.0$/, '') + '"'; }
  function ftin(x) { var ft = Math.floor(x / 12), i = Math.round(x - ft * 12); if (i === 12) { ft++; i = 0; } return ft + "' " + i + '"'; }
  function lbs(x) { return Math.round(x).toLocaleString('en-US') + ' lb'; }
  // vehicle value: {min, max, sure}. sure = typed by customer or exact manufacturer spec of this version.
  function vval(v, k) {
    var x = v && v[k];
    if (x == null) return null;
    if (typeof x === 'number') return { min: x, max: x, sure: !!(v.src === 'user' || v.exact) };
    var mn = num(x.min), mx = num(x.max);
    if (mn == null && mx == null) return null;
    if (mn == null) mn = mx; if (mx == null) mx = mn;
    return { min: mn, max: mx, sure: !!(x.user || v.src === 'user' || v.exact) };
  }
  function gval(g, k) { var x = g && num(g[k]); return x == null || x <= 0 ? null : x; }
  function chk(key, status, text, extra) { var c = { key: key, status: status, text: text }; if (extra) for (var e in extra) c[e] = extra[e]; return c; }

  // ---- individual checks ---------------------------------------------------------------------------------
  function capacity(lift, v) {
    var cap = fv(lift, 'capacity_lbs'), w = vval(v, 'weight_lbs');
    if (cap == null) return chk('capacity', 'cond', 'Lift capacity not listed — we\'ll confirm.');
    if (!w) return chk('capacity', 'cond', 'Add your vehicle weight to check capacity (lift rated ' + lbs(cap) + ').');
    var cushion = Math.round((cap - w.max) / w.max * 100); // % above vehicle weight (same wording as the market: "60% capacity cushion")
    var ex = { cushion: cushion, cap: cap };
    if (w.min > cap * (1 + TOL.weightPct / 100)) {
      var sure = w.sure && fromMfr(lift, 'capacity_lbs');
      return chk('capacity', sure ? 'no' : 'cond', 'Vehicle (' + lbs(w.min) + (w.max > w.min ? '+' : '') + ') is heavier than the lift rating (' + lbs(cap) + ').', ex);
    }
    if (w.max > cap) return chk('capacity', 'cond', 'Some versions of this vehicle (up to ' + lbs(w.max) + ') exceed the ' + lbs(cap) + ' rating — check your exact weight.', ex);
    if (cushion < 20) return chk('capacity', 'cond', 'Only ' + cushion + '% capacity cushion (' + lbs(cap) + ' rating) — little margin for cargo or tools.', ex);
    return chk('capacity', 'ok', cushion + '% capacity cushion', ex);
  }

  function liftHeights(lift) {
    var lo = fv(lift, 'overall_height_in'), hi = fv(lift, 'overall_height_max_in');
    if (lo != null && hi != null && hi < lo) { var t = lo; lo = hi; hi = t; }
    return { lo: lo, hi: hi == null ? lo : hi };
  }

  function ceilingFrame(lift, g) {
    var ceil = gval(g, 'ceiling_in'), h = liftHeights(lift);
    if (ceil == null) return null;
    if (h.lo == null) {
      if (lift.category === 'two_post' || lift.category === 'four_post' || lift.category === 'single_post')
        return chk('ceiling', 'cond', 'Lift overall height not listed — we\'ll confirm it fits under your ' + ftin(ceil) + ' ceiling.');
      return null; // scissor / portable lifts sit low; covered by the raised-vehicle check
    }
    if (h.lo > ceil + TOL.ceiling)
      return chk('ceiling', fromMfr(lift, 'overall_height_in') ? 'no' : 'cond', 'Lift is ' + inch(h.lo) + ' tall — taller than your ' + inch(ceil) + ' ceiling.');
    if (h.hi > ceil + TOL.ceiling)
      return chk('ceiling', 'cond', 'Use a lower column setting: ' + inch(h.lo) + '–' + inch(h.hi) + ' tall vs your ' + inch(ceil) + ' ceiling.');
    if (ceil - h.hi < 3) return chk('ceiling', 'cond', 'Only ' + inch(ceil - h.hi) + ' between lift top and ceiling — check door opener and lights.');
    return chk('ceiling', 'ok', 'fits ' + inch(ceil) + ' ceiling');
  }

  // top of the vehicle at full rise (upper bound, conservative)
  function roofAtFullRise(lift, vh) {
    var rise = fv(lift, 'max_rise_in');
    if (rise == null) return null;
    if (lift.category === 'two_post' || lift.category === 'scissor_mid_rise' || lift.category === 'scissor_low_rise') {
      // frame-engaging: roof rises by (rise - pickup point height) <= rise - lowest pad height
      var pad = fv(lift, 'min_pad_height_in');
      return vh + rise - (pad == null ? 0 : pad);
    }
    return vh + rise; // drive-on: rise = top of runway / platform
  }

  function raisedVehicle(lift, v, g) {
    var ceil = gval(g, 'ceiling_in'), vh = vval(v, 'height_in'), rise = fv(lift, 'max_rise_in');
    if (ceil == null || rise == null || lift.category === 'motorcycle') return null;
    if (!vh) return chk('raise', 'cond', 'Add your vehicle height to check how high it can go under your ' + inch(ceil) + ' ceiling.');
    var top = roofAtFullRise(lift, vh.max), margin = 2, offset = top - vh.max - rise; // offset = -pad for frame-engaging lifts
    if (top + margin <= ceil) return chk('raise', 'ok', 'full ' + inch(rise) + ' rise clears your ceiling');
    var usable = Math.max(0, Math.min(rise, ceil - margin - vh.max - offset));
    if (usable < 6) return chk('raise', 'cond', 'With this vehicle there is almost no room to raise it under your ' + inch(ceil) + ' ceiling.', { usable_rise: r1(usable) });
    return chk('raise', 'cond', 'With this vehicle the lift can go up to about ' + inch(r1(usable)) + ' of its ' + inch(rise) + ' rise before the roof nears your ceiling.', { usable_rise: r1(usable) });
  }

  function overhead(lift, v) {
    if (lift.category !== 'two_post' || lift.top !== 'overhead') return null;
    var oc = fv(lift, 'overhead_clearance_in'), vh = vval(v, 'height_in');
    if (oc == null || !vh) return null;
    if (vh.min > oc + TOL.overhead)
      return chk('overhead', vh.sure && fromMfr(lift, 'overhead_clearance_in') ? 'no' : 'cond', 'Vehicle height ' + inch(vh.min) + ' is above the overhead bar clearance (' + inch(oc) + ').');
    var rise = fv(lift, 'max_rise_in'), pad = fv(lift, 'min_pad_height_in') || 0;
    if (rise != null) {
      var usable = Math.max(0, Math.min(rise, oc - 1 - vh.max + pad)); // roof moves up with the pads
      if (usable < rise && usable < 66) return chk('overhead', 'cond', 'The roof reaches the overhead shut-off bar (' + inch(oc) + ') after about ' + inch(r1(usable)) + ' of lift — a floor-plate or clear-floor lift may suit this vehicle better.', { usable_rise: r1(usable) });
      if (usable < rise) return chk('overhead', 'ok', 'overhead bar allows ~' + inch(r1(usable)) + ' of rise');
    } else if (vh.max > oc - 6) return chk('overhead', 'cond', 'Roof will be close to the overhead shut-off bar (' + inch(oc) + ').');
    return chk('overhead', 'ok', 'roof clears overhead bar (' + inch(oc) + ')');
  }

  function bay(lift, g) {
    var out = [], bw = gval(g, 'bay_width_in'), bl = gval(g, 'bay_length_in');
    var ow = fv(lift, 'overall_width_in');
    if (bw != null) {
      if (ow == null) out.push(chk('bay_width', 'cond', 'Lift overall width not listed — we\'ll confirm it fits your ' + inch(bw) + ' bay.'));
      else if (ow > bw + TOL.width) out.push(chk('bay_width', fromMfr(lift, 'overall_width_in') ? 'no' : 'cond', 'Lift is ' + inch(ow) + ' wide — wider than your ' + inch(bw) + ' bay.'));
      else if (bw - ow < 24) out.push(chk('bay_width', 'cond', 'Only ' + inch(bw - ow) + ' spare width beside the lift (' + inch(ow) + ' wide) — allow room to walk and open doors.'));
      else out.push(chk('bay_width', 'ok', 'fits ' + inch(bw) + ' bay width'));
    }
    if (bl != null && (lift.category === 'four_post' || lift.category === 'single_post' || lift.category === 'scissor_full_rise')) {
      var olNo = fv(lift, 'overall_length_noramps_in'), ol = fv(lift, 'overall_length_in');
      if (olNo != null && olNo > bl + TOL.length)
        out.push(chk('bay_length', fromMfr(lift, 'overall_length_noramps_in') ? 'no' : 'cond', 'Lift is ' + inch(olNo) + ' long — longer than your ' + inch(bl) + ' bay.'));
      else if (ol != null && ol > bl + TOL.length)
        out.push(chk('bay_length', 'cond', 'Lift is ' + inch(ol) + ' long with ramps vs your ' + inch(bl) + ' bay — ramps may need to be removed after parking.'));
      else if (ol == null && olNo == null) out.push(chk('bay_length', 'cond', 'Lift length not listed — we\'ll confirm it fits your ' + inch(bl) + ' bay depth.'));
      else out.push(chk('bay_length', 'ok', 'fits ' + inch(bl) + ' bay depth'));
    }
    return out;
  }

  function driveThru(lift, v) {
    if (lift.category !== 'two_post' && lift.category !== 'four_post') return null;
    var dt = fv(lift, 'drive_thru_in'), k = 'drive_thru_in', vw = vval(v, 'width_in');
    if (dt == null) { dt = fv(lift, 'width_between_columns_in'); k = 'width_between_columns_in'; if (lift.category === 'two_post') dt = null; }
    if (!vw) return null;
    if (dt == null) return chk('drive_thru', 'cond', 'Drive-through width not listed — we\'ll confirm your ' + inch(vw.max) + '-wide vehicle passes.');
    var m = dt - vw.max;
    var wsure = vw.sure && (v.src === 'user' || (v.width_in && v.width_in.user) || v.width_excl_mirrors); // DB width must be stated "excluding mirrors"
    if (vw.min > dt + TOL.driveThru)
      return chk('drive_thru', wsure && fromMfr(lift, k) ? 'no' : 'cond', 'Vehicle body (' + inch(vw.min) + ' wide) is wider than the drive-through (' + inch(dt) + ').');
    if (m < 6) return chk('drive_thru', 'cond', 'Tight drive-through: ' + inch(dt) + ' vs vehicle ' + inch(vw.max) + ' (mirrors folded).');
    return chk('drive_thru', 'ok', inch(r1(m)) + ' drive-through margin');
  }

  function wheelbase(lift, v) {
    var wb = vval(v, 'wheelbase_in'); if (!wb || lift.category === 'motorcycle') return null;
    var mn = fv(lift, 'wheelbase_min_in'), mx = fv(lift, 'wheelbase_max_in');
    if (mx != null && wb.min > mx + TOL.wheelbase)
      return chk('wheelbase', wb.sure && fromMfr(lift, 'wheelbase_max_in') ? 'no' : 'cond', 'Wheelbase ' + inch(wb.min) + ' is longer than the lift maximum (' + inch(mx) + ').');
    if (mn != null && wb.max < mn - TOL.wheelbase)
      return chk('wheelbase', wb.sure && fromMfr(lift, 'wheelbase_min_in') ? 'no' : 'cond', 'Wheelbase ' + inch(wb.max) + ' is shorter than the lift minimum (' + inch(mn) + ').');
    if (mx != null || mn != null) {
      if ((mx != null && wb.max > mx) || (mn != null && wb.min < mn)) return chk('wheelbase', 'cond', 'Wheelbase is at the edge of the lift range — we\'ll confirm.');
      return chk('wheelbase', 'ok', 'wheelbase OK');
    }
    if (lift.category === 'four_post') {
      var rl = fv(lift, 'runway_length_in');
      if (rl == null) return chk('wheelbase', 'cond', 'Runway length not listed — we\'ll confirm your ' + inch(wb.max) + ' wheelbase.');
      if (wb.min > rl + TOL.runway)
        return chk('wheelbase', wb.sure && fromMfr(lift, 'runway_length_in') ? 'no' : 'cond', 'Wheelbase ' + inch(wb.min) + ' is longer than the ' + inch(rl) + ' runways.');
      if (wb.max > rl - 30) return chk('wheelbase', 'cond', 'Long wheelbase for ' + inch(rl) + ' runways — we\'ll confirm tire placement.');
      return chk('wheelbase', 'ok', 'wheelbase OK (' + inch(rl) + ' runways)');
    }
    if (lift.category === 'two_post' && wb.max > 160) return chk('wheelbase', 'cond', 'Long wheelbase (' + inch(wb.max) + ') — we\'ll confirm arm reach and balance on this lift.');
    return null;
  }

  function lowCar(lift, v) {
    var gc = vval(v, 'ground_clearance_in'); if (!gc) return null;
    var pad = fv(lift, 'min_pad_height_in');
    if ((lift.category === 'two_post' || lift.category === 'scissor_mid_rise' || lift.category === 'scissor_low_rise') && pad != null && pad > gc.min - 0.5)
      return chk('low', 'cond', 'Lowest pad height ' + inch(pad) + ' vs ground clearance ' + inch(gc.min) + ' — low cars may need low-profile arms or pads.');
    if (lift.category === 'four_post' && gc.min < 5) return chk('low', 'cond', 'Low car — approach ramps may need extensions.');
    return null;
  }

  function storage(lift, v, g) {
    var h2 = gval(g, 'under_car_height_in');
    if (h2 == null || (lift.category !== 'four_post' && lift.category !== 'single_post')) return null;
    var under = fv(lift, 'max_under_runway_in'), ceil = gval(g, 'ceiling_in'), vh = vval(v, 'height_in'), rise = fv(lift, 'max_rise_in');
    if (under == null) return chk('storage', 'cond', 'Clearance under the runways not listed — we\'ll confirm parking a ' + inch(h2) + ' car underneath.');
    if (under < h2 - TOL.underCar)
      return chk('storage', fromMfr(lift, 'max_under_runway_in') ? 'no' : 'cond', 'Max clearance under runways ' + inch(under) + ' is less than your lower car (' + inch(h2) + ').');
    if (ceil != null && vh && rise != null && vh.max + rise + 2 > ceil)
      return chk('storage', 'cond', 'Your ceiling limits how high the top car can go — we\'ll confirm the ' + inch(h2) + ' car fits underneath.');
    if (under - h2 < 2) return chk('storage', 'cond', 'Only ' + inch(under - h2) + ' above the lower car — tight.');
    return chk('storage', 'ok', inch(under) + ' under runways for the lower car');
  }

  function power(lift, g) {
    var p = g && g.power; if (!p || p === 'unknown') return null;
    var s = String(fs(lift, 'voltage') || '').toLowerCase();
    if (!s) return chk('power', 'cond', 'Power requirement not listed — we\'ll confirm.');
    var has110 = /\b11[05]\s*v|\b120\s*v|110\/|\b110\b|\b115\b|\b120\b/.test(s), has220 = /2[0-4]\d\s*v|\b2[0-4]\d\b/.test(s), only3 = /three|3[- ]?ph/.test(s) && !/single|1[- ]?ph/.test(s);
    if (p === '120' && !has110) return chk('power', 'cond', 'Needs a ' + (only3 ? 'three-phase' : '220–240V') + ' circuit (' + fs(lift, 'voltage') + ') — an electrician can add one.');
    if (p === '240_1' && only3) return chk('power', 'cond', 'Lift power unit is three-phase (' + fs(lift, 'voltage') + ') — ask us about single-phase options.');
    return chk('power', 'ok', 'power OK');
  }

  function air(lift, g) {
    if (!g || g.air !== 'no') return null;
    var a = fs(lift, 'air_required');
    if (a === true || a === 'true' || a === 1) return chk('air', 'cond', 'This lift needs shop air (air-release locks) — plan for a small compressor.');
    return null;
  }

  function floor(lift, g) {
    var t = gval(g, 'slab_in'), need = fv(lift, 'concrete_min_thickness_in'), psi = fv(lift, 'concrete_min_psi');
    if (need == null) return null;
    var req = inch(need) + (psi ? ', ' + Math.round(psi) + ' PSI' : '') + ' concrete';
    if (t == null) return chk('floor', 'info', 'Needs ' + req + '.');
    if (t < need) return chk('floor', 'cond', 'Needs ' + req + ' — your slab is ' + inch(t) + '. Thicker footings may be required.');
    return chk('floor', 'ok', 'slab OK (' + req + ')');
  }

  function typeFit(lift, v, pref) {
    var out = [];
    if (pref && pref.type && pref.type !== 'any') {
      var m = pref.type === 'scissor' ? /^scissor/.test(lift.category) : pref.type === 'portable' ? !!lift.portable : lift.category === pref.type;
      if (!m) out.push(chk('type', 'skip', 'Not the lift type you chose.'));
    }
    if (lift.category === 'two_post' && v && v.body) {
      var truck = /pickup|truck|van/.test(v.body);
      if (truck && lift.arm_config === 'asymmetric') out.push(chk('arms', 'info', 'Asymmetric arms suit cars; long trucks balance better on symmetric or versatile arms.'));
    }
    return out;
  }

  // ---- main ----------------------------------------------------------------------------------------------
  function evaluate(lift, vehicle, garage, pref) {
    var v = vehicle || {}, g = garage || {};
    if (v.body === 'motorcycle' ? lift.category !== 'motorcycle' : lift.category === 'motorcycle') return { status: 'skip', checks: [] };
    var checks = [capacity(lift, v), ceilingFrame(lift, g), overhead(lift, v), raisedVehicle(lift, v, g), driveThru(lift, v), wheelbase(lift, v), lowCar(lift, v), storage(lift, v, g), power(lift, g), air(lift, g), floor(lift, g)]
      .concat(bay(lift, g), typeFit(lift, v, pref)).filter(Boolean);
    var status = 'fit';
    checks.forEach(function (c) {
      if (c.status === 'skip') status = 'skip';
      else if (status !== 'skip' && c.status === 'no') status = 'no';
      else if (status === 'fit' && c.status === 'cond') status = 'cond';
    });
    var cap = checks.filter(function (c) { return c.key === 'capacity'; })[0];
    return { status: status, checks: checks, cushion: cap && cap.cushion != null ? cap.cushion : null };
  }

  function score(lift, res, v, pref) {
    var s = res.status === 'fit' ? 100 : res.status === 'cond' ? 60 : res.status === 'no' ? 0 : -100;
    var cu = res.cushion;
    if (cu != null) s += cu < 20 ? 0 : cu <= 150 ? 20 : Math.max(0, 20 - (cu - 150) / 10); // oversized lifts drift down
    var use = pref && pref.use;
    var c = lift.category;
    if (use === 'storage') s += (c === 'four_post' || c === 'single_post') ? 25 : -30; // only drive-on post lifts can park a car underneath
    else if (use === 'service') s += (c === 'two_post' || c === 'scissor_full_rise') ? 25 : c === 'four_post' && lift.four_post_use !== 'storage' ? 10 : 0;
    else if (use === 'both') s += c === 'four_post' ? 25 : c === 'two_post' ? 15 : 0;
    else if (use === 'wheels') s += (c === 'scissor_mid_rise' || c === 'scissor_low_rise' || lift.portable) ? 25 : c === 'two_post' ? 10 : 0;
    if (c === 'two_post' && v && v.body) {
      if (/pickup|truck|van/.test(v.body) && lift.arm_config === 'symmetric') s += 5;
      if (/car/.test(v.body) && lift.arm_config === 'asymmetric') s += 5;
    }
    var n = 0, have = 0; ['capacity_lbs', 'overall_height_in', 'max_rise_in', 'overall_width_in'].forEach(function (k) { n++; if (fv(lift, k) != null) have++; });
    s += 10 * have / n;
    s -= res.checks.filter(function (x) { return x.status === 'cond'; }).length * 3;
    var rz = res.checks.filter(function (x) { return x.usable_rise != null; })[0], rise = fv(lift, 'max_rise_in');
    if (rz && rise) s -= 25 * (1 - Math.min(rz.usable_rise, rise) / rise); // prefer lifts whose rise the ceiling does not cut off
    return s;
  }

  // lifts: {handle: lift}; returns sorted list [{handle, lift, res, display, score}]
  function rank(lifts, vehicle, garage, pref, opts) {
    var showNo = !!(opts && opts.showNoFit), list = [];
    Object.keys(lifts).forEach(function (h) {
      var lift = lifts[h], res = evaluate(lift, vehicle, garage, pref);
      if (res.status === 'skip') return;
      list.push({ handle: h, lift: lift, res: res, display: res.status === 'no' && !showNo ? 'cond' : res.status, score: score(lift, res, vehicle, pref) });
    });
    list.sort(function (a, b) { return b.score - a.score || (fv(a.lift, 'capacity_lbs') || 0) - (fv(b.lift, 'capacity_lbs') || 0); });
    // top picks: no more than 2 of the same category unless the customer picked a type
    var picks = [], cats = {};
    var anyType = !(pref && pref.type && pref.type !== 'any');
    for (var i = 0; i < list.length && picks.length < 3; i++) {
      var it = list[i]; if (it.res.status === 'no') continue;
      if (anyType && (cats[it.lift.category] || 0) >= 2) continue;
      if (pref && pref.use === 'storage' && !/^(four_post|single_post)$/.test(it.lift.category)) continue; // parking needs a drive-on post lift
      cats[it.lift.category] = (cats[it.lift.category] || 0) + 1; picks.push(it);
    }
    var rest = list.filter(function (x) { return picks.indexOf(x) < 0; });
    // vehicle (lowest published height) already reaches the ceiling the customer entered: no lift can raise it there
    var ceil = gval(garage, 'ceiling_in'), vh = vval(vehicle, 'height_in'), blocked = null;
    if (ceil != null && vh && vh.min + 2 >= ceil) blocked = { vehicle_height_in: vh.min, ceiling_in: ceil, sure: vh.sure };
    return { picks: picks, rest: rest, all: list, blocked: blocked };
  }

  function reasons(item) {
    var ok = item.res.checks.filter(function (c) { return c.status === 'ok'; }).map(function (c) { return c.text; });
    return ok.slice(0, 4);
  }

  var api = { evaluate: evaluate, rank: rank, score: score, reasons: reasons, CAT_LABEL: CAT_LABEL, TOL: TOL, ftin: ftin, inch: inch, lbs: lbs, fv: fv, fs: fs };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.VLLift = api;
})(typeof window !== 'undefined' ? window : this);
